// Stock's Catalog and prices (Blueprint only; pages/ux-full accounting/stock/catalog): the price book's
// parts as stock keeps them, drawn with ui-web's CatalogTable over stock.catalog, a page at a time, by
// category, type or a search. A part opens Stock's own sheet (ItemSheet: what the vendor sells it in,
// its vendor, bin and levels; stock.setItem and stock.setLevels keep their checks). The prices are the
// price book's: the same items as Setup › Price book, changed there, and each page links to the other.
import { useEffect, useState } from 'react';
import { CatalogTable, Section, type CatalogPart } from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { holdsAny } from '../shared/may.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { useQuery, useToast } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { usePaged } from '../blueprint/paged.ts';
import '../blueprint/list-pages.css';
// Stock's own sheet, drawn as Stock draws it
import '../books/books.css';
import '../books/blueprint.css';
import './inventory.css';
import './blueprint.css';
import { ItemSheet } from './ItemSheet.tsx';
import { orderLocations, STOCK_PERMS, type StockLocation } from './model.ts';
import { ORDER_PERMS } from './orders-model.ts';
import {
  CATALOG_TYPES,
  catalogInput,
  categoryOptions,
  opensPriceBook,
  PRICE_BOOK_LINK,
  type CatalogPage,
  type CatalogType,
  type Category,
} from './catalog-model.ts';

const rowsOf = (a: CatalogPage) => a.parts;

export function CatalogScreen({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  const [typed, setTyped] = useState('');
  // the search asks once typing pauses
  const [query, setQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed), 250);
    return () => clearTimeout(t);
  }, [typed]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [type, setType] = useState<CatalogType>('all');
  const [open, setOpen] = useState<CatalogPart | null>(null);
  const [toast, setToast] = useToast();
  const list = usePaged('stock.catalog', catalogInput(query, categoryId, type), rowsOf);
  const cats = useQuery<{ categories: Category[] }>('pricebook.listCategories', {});
  const locs = useQuery<{ locations: StockLocation[] }>('stockLocations.list', {});
  const options = cats.data ? categoryOptions(cats.data.categories) : [];
  const priceBook = opensPriceBook(held);
  return (
    <div className="app bp-lp">
      <Header
        nav={nav}
        title="Catalog and prices"
        lede="The price book’s parts as stock keeps them: each in its own unit and bought in the vendor’s, with its bin, our cost and its price."
      >
        {priceBook && (
          <a className="bp-lp-link" href={PRICE_BOOK_LINK}>
            The price book
          </a>
        )}
      </Header>
      <main className="page bp-lp-page flw flw-office">
        <div className="bp-lp-filters">
          <div className="bp-lp-seg" role="group" aria-label="Show">
            {CATALOG_TYPES.map((t) => (
              <button
                key={t.type}
                type="button"
                aria-pressed={type === t.type}
                onClick={() => setType(t.type)}
              >
                {t.label}
              </button>
            ))}
          </div>
          {options.length > 0 && (
            <label className="bp-lp-pick">
              <span>Category</span>
              <select value={categoryId ?? ''} onChange={(e) => setCategoryId(e.target.value || null)}>
                <option value="">Every category</option>
                {options.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="bp-lp-pick">
            <span>Find a part</span>
            <input
              type="search"
              placeholder="Name or code"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
        </div>
        {toast && (
          <p className="toast" role="status">
            {toast}
          </p>
        )}
        <div className="bp-lp-cols">
          <div>
            <Section title="Parts">
              {list.rows ? (
                <CatalogTable
                  key={JSON.stringify(catalogInput(query, categoryId, type))}
                  data={{ parts: list.rows }}
                  onOpen={setOpen}
                  {...(list.more ? { onMore: list.more } : {})}
                  {...(query.trim() || categoryId || type !== 'all'
                    ? { empty: 'No part matches. Try another category, or fewer words.' }
                    : {})}
                />
              ) : (
                <Loading error={list.error} what="the catalog" kind="table" retry={list.reload} />
              )}
            </Section>
          </div>
          <div>
            <Section title="One part">
              <p className="flw-line">
                Open a part to change what the vendor sells it in, its vendor, its bin, and its minimum and
                maximum where it’s kept.
              </p>
              <p className="flw-sub">
                These are the price book’s own items, not a copy: a part’s name, price and cost are changed in
                Setup › Price book, and show here at once. Our own parts, kept and used but never sold, are
                there too, marked not for sale.
              </p>
            </Section>
          </div>
        </div>
      </main>
      {open && locs.data && (
        <div className="books inventory">
          <ItemSheet
            itemId={open.id}
            itemName={open.name}
            locations={orderLocations(locs.data.locations)}
            mayManage={holdsAny(held, STOCK_PERMS)}
            mayVendors={holdsAny(held, ORDER_PERMS)}
            onClose={() => setOpen(null)}
            onChanged={(said) => {
              setToast(said);
              list.reload();
            }}
          />
        </div>
      )}
    </div>
  );
}
